import { type Registry as Services, service } from '@ember/service';
import Model, { attr, type HasMany, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { ConditionalStrategy } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import {
  COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY,
  COMPETITORS_IMPACTS_STRATEGY_LEVELS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import {
  getDaysWithPriceRangeError,
  getMultiRoomDaysWithPriceRangeError,
} from 'lumen/utils/pricing-assistant/recommendation-settings';

export type ComputedValueCondition = {
  condition_type: 'computed_value';
  name: string;
};

export type UserEditableCondition = {
  condition_type: 'user_editable_value';
  value: number | null;
};

export type OperandCondition = {
  condition_type: 'operand';
  first: OperandCondition | ComputedValueCondition | UserEditableCondition;
  operation: string;
  second: OperandCondition | ComputedValueCondition | UserEditableCondition;
};

export type StrategyCondition =
  | OperandCondition
  | ComputedValueCondition
  | UserEditableCondition;

export type PriceRangeSettings = {
  day_of_week: {
    [key: number]: {
      maximum: number | null;
      minimum: number | null;
    };
  };
  overrides: {
    [dayOfWeek: number]: {
      [roomId: string]: {
        maximum: number | null;
        minimum: number | null;
      };
    };
  };
  settings_type: 'price_range';
};

export type MinimumPriceDifferenceSettings = {
  day_of_week: {
    [key: number]: number | null;
  };
  enabled: boolean;
  settings_type: 'minimum_difference';
};

export type AllowedRecommendationTypeSettings = {
  allowed: 'all' | 'increase' | 'decrease';
  settings_type: 'allowed_recommendation_type';
};

export type RoundingSettings = {
  enabled: boolean;
  rounding_values: number[];
  settings_type: 'rounding';
};

export type RoomOffsetHierarchy = {
  children: Record<string, RoomOffsetHierarchy>;
  offset: { type: string; value: number };
};

export type RoomOffsetHierarchySettings = {
  hierarchy: Record<string, RoomOffsetHierarchy>;
  settings_type: 'room_offset_hierarchy';
};

export type CompsetPositioningLevelValues =
  | (typeof COMPETITORS_IMPACTS_STRATEGY_LEVELS)[keyof typeof COMPETITORS_IMPACTS_STRATEGY_LEVELS]
  | typeof COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY; // FE only state to mark undropped competitors during initial assignment

export type CompsetPositioningDayOfWeekSettings = {
  [key: string]: CompsetPositioningLevelValues;
};

export type CompsetPositioningSettings = {
  day_of_week: {
    [key: number]: CompsetPositioningDayOfWeekSettings;
  };
  settings_type: 'compset_positioning';
};

export type RollingReferenceRatesSettings = {
  day_of_week: {
    [dayOfWeek: number]: {
      [roomId: string]: number | null;
    };
  };
  settings_type: 'rolling_reference_rates';
};

export default class RecommendationStrategy extends Model {
  [Type] = 'recommendation-strategy' as const;

  @hasMany('conditional-strategy', { async: false, inverse: null })
  declare conditionalStrategies: HasMany<ConditionalStrategy>;

  @attr() declare conditions: StrategyCondition | null;
  @attr('dayjs') declare createdAt: DayjsTransformReturn;
  @attr('string') declare name: string;
  @attr('hoteldate') declare seasonEndDt: HoteldateTransformReturn;
  @attr('hoteldate') declare seasonStartDt: HoteldateTransformReturn;

  @attr() declare settings:
    | PriceRangeSettings
    | RoundingSettings
    | CompsetPositioningSettings
    | AllowedRecommendationTypeSettings
    | MinimumPriceDifferenceSettings
    | RoomOffsetHierarchySettings
    | RollingReferenceRatesSettings;

  @attr('string') declare settingsType: (typeof this.settings)['settings_type'];

  @attr('number') declare subscriptionId: number;
  @attr('dayjs') declare updatedAt: DayjsTransformReturn;
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetPositioningCompetitorsLeftToAssignInStartingState() {
    if (
      this.settings.settings_type === 'compset_positioning' &&
      this.isCompsetPositioningStartingState
    ) {
      const defaultImpactedDays: number[] = [1, 2, 3, 4, 5, 6, 7];

      const competitorImpactSettings = this.settings.day_of_week;

      const dowSettings = competitorImpactSettings[
        defaultImpactedDays[0] as number
      ] as CompsetPositioningDayOfWeekSettings;

      const competitorIds = Object.keys(dowSettings).filter((competitorId) => {
        return (
          dowSettings[parseInt(competitorId, 10)] ===
          COMPETITOR_IMPACT_STRATEGY_INIT_EMPTY
        );
      });

      return competitorIds;
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get doesCompsetPositioningHaveInvalidSelection() {
    if (this.settings.settings_type === 'compset_positioning') {
      const competitorImpactSettings = this.settings.day_of_week;

      const daysWithInvalidSelection = Object.keys(
        competitorImpactSettings,
      ).filter((dow) => {
        const dowSettings = competitorImpactSettings[
          parseInt(dow, 10)
        ] as CompsetPositioningDayOfWeekSettings;
        return Object.values(dowSettings).every(
          (value) => value === COMPETITORS_IMPACTS_STRATEGY_LEVELS.IRRELEVANT,
        );
      });

      return {
        hasInvalidSelection: daysWithInvalidSelection.length > 0,
        daysWithInvalidSelection,
      };
    }

    return {
      hasInvalidSelection: false,
      daysWithInvalidSelection: [],
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get errorMessageForInvalidStrategy() {
    if (this.settingsType === 'price_range') {
      return this.intl.t(
        'pricing_manager.recommendations.min_higher_than_max_error',
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCompsetPositioningStartingState() {
    if (
      this.settingsType === 'compset_positioning' &&
      this.isDefaultStrategy &&
      this.createdAt &&
      this.updatedAt
    ) {
      // Check if difference between createdAt and updatedAt is less than 1 second
      // Currently this is the only way to tell if a default strategy is defined by BE or by the user
      // This is relevant for a custom start state on the Recommendations compset positioning settings page
      return Math.abs(this.createdAt.diff(this.updatedAt, 'seconds')) < 1;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCompsetPositioningStartingStateInvalid() {
    return (
      this.settingsType === 'compset_positioning' &&
      this.isCompsetPositioningStartingState &&
      this.compsetPositioningCompetitorsLeftToAssignInStartingState.length > 0
    );
  }

  // Default strategy is the one without start and end date
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDefaultStrategy() {
    return !this.seasonStartDt && !this.seasonEndDt && this.name === 'default';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSeasonalStrategy() {
    return !this.isDefaultStrategy;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isStrategyInvalid() {
    if (this.settings.settings_type !== 'price_range') {
      return (
        this.isCompsetPositioningStartingStateInvalid ||
        this.doesCompsetPositioningHaveInvalidSelection.hasInvalidSelection
      );
    }

    if (this.access.hasPricingAssistantOpenPricingFeature) {
      // Multi-Room price range
      return this.multiRoomPriceRangeStrategyDayOfWeekWithErrors.length > 0;
    }

    // Single room price range
    return this.priceRangeStrategyDayOfWeekWithErrors.length > 0;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get multiRoomPriceRangeStrategyDayOfWeekWithErrors() {
    const { settings } = this;

    if (
      settings.settings_type !== 'price_range' ||
      !this.access.hasPricingAssistantOpenPricingFeature
    ) {
      return [];
    }

    const daysAndRoomsWithPriceRangeError =
      getMultiRoomDaysWithPriceRangeError(settings);

    return daysAndRoomsWithPriceRangeError;
  }

  /*
  Each table cell checks if it belongs to a day of week with errors. This causes this getter to be called multiple times.
  To avoid recalculating the same value, we cache it.
  */
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceRangeStrategyDayOfWeekWithErrors() {
    const { conditionalStrategies, settings } = this;

    if (
      settings.settings_type !== 'price_range' ||
      this.access.hasPricingAssistantOpenPricingFeature
    ) {
      return [];
    }

    const daysWithPriceRangeError = getDaysWithPriceRangeError(settings, false);

    if (
      conditionalStrategies.length === 0 ||
      !conditionalStrategies[0]!.settings
    ) {
      return daysWithPriceRangeError;
    }

    const conditionalSettings = conditionalStrategies[0]!.settings;

    const conditionalDaysWithPriceRangeError =
      conditionalSettings.settings_type === 'price_range'
        ? getDaysWithPriceRangeError(conditionalSettings, true)
        : [];

    return [...daysWithPriceRangeError, ...conditionalDaysWithPriceRangeError];
  }
}
